import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useRef, useState } from 'react'

type StreamedQueryOptions<TData, TChunk> = {
    queryKey: unknown[]
    streamFn: (signal: AbortSignal) => AsyncIterable<TChunk>
    reducer: (prev: TData | undefined, chunk: TChunk) => TData
    initialData?: TData
    enabled?: boolean
    refetchOnMount?: boolean
    /** Called after all chunks are processed; can recompute derived state (e.g. highlights) from full data. */
    onStreamEnd?: (data: TData) => TData
} & Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn' | 'enabled'>

function useStreamedQuery<TData, TChunk>(
    options: StreamedQueryOptions<TData, TChunk>
) {
    const abortControllerRef = useRef<AbortController | null>(null)

    // Track streaming data separately so components can see updates in real-time
    const [streamingData, setStreamingData] = useState<TData | undefined>(
        options.initialData
    )

    const {
        queryKey,
        streamFn,
        reducer,
        initialData,
        enabled,
        refetchOnMount,
        onStreamEnd,
        ...restOptions
    } = options

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            // Cancel previous stream if exists (for same query key)
            const previousController = abortControllerRef.current
            if (previousController) {
                previousController.abort()
            }

            const currentController = new AbortController()
            abortControllerRef.current = currentController

            let accumulatedData = initialData
            const signal = currentController.signal

            // Reset streaming data when starting new query
            setStreamingData(initialData)

            // Batch cache updates to prevent memory issues
            let updateTimer: ReturnType<typeof setTimeout> | undefined =
                undefined
            let chunkCount = 0
            const UPDATE_INTERVAL_MS = 1000
            const UPDATE_CHUNK_INTERVAL = 100

            const clearUpdateTimer = () => {
                if (updateTimer !== undefined) {
                    clearTimeout(updateTimer)
                    updateTimer = undefined
                }
            }

            const updateData = (data: TData | undefined) => {
                // Update both state (for immediate re-renders) and cache
                setStreamingData(data)
            }

            const scheduleCacheUpdate = () => {
                clearUpdateTimer()
                updateTimer = setTimeout(() => {
                    updateData(accumulatedData)
                    updateTimer = undefined
                }, UPDATE_INTERVAL_MS)
            }

            try {
                for await (const chunk of streamFn(signal)) {
                    if (signal.aborted) {
                        break
                    }

                    // Apply reducer to accumulate data
                    accumulatedData = reducer(accumulatedData, chunk)
                    chunkCount++

                    // Update data periodically (throttled)
                    if (chunkCount % UPDATE_CHUNK_INTERVAL === 0) {
                        // Immediate update every N chunks
                        clearUpdateTimer()
                        updateData(accumulatedData)
                    } else {
                        // Otherwise schedule a batched update
                        scheduleCacheUpdate()
                    }
                }

                // Final pass so derived state (e.g. highlights) considers all chunks
                if (onStreamEnd) {
                    accumulatedData = onStreamEnd(accumulatedData as TData)
                }

                // Ensure final state is written
                clearUpdateTimer()
                updateData(accumulatedData)

                return accumulatedData as TData
            } catch (error) {
                // Cancel any pending updates
                clearUpdateTimer()
                // Only throw if not aborted and it's the current controller
                if (!signal.aborted) {
                    throw error
                }
                // If aborted, run onStreamEnd on last accumulated data then return
                if (onStreamEnd && accumulatedData !== undefined) {
                    accumulatedData = onStreamEnd(accumulatedData as TData)
                }
                return accumulatedData as TData
            }
        },
        enabled,
        refetchOnMount,
        ...restOptions,
    })

    // Return query with streaming data merged in
    // Use streamingData while fetching, query.data when complete
    return {
        ...query,
        data: query.data ?? streamingData,
    }
}

export default useStreamedQuery
